import React from "react";
import { Card, Row, Col, Descriptions, Tabs, Table } from "antd";

const StripeTransactionDetails = ({ transaction }) => {
  // Format currency based on the currency code
  const formatCurrency = (amount, currency = 'USD') => {
    // Convert from cents to dollars for display
    const value = amount / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(value);
  };

  // Format date to local string
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  // Get payment method details in a readable format
  const getPaymentMethodDetails = () => {
    if (!transaction.payment_method_details) return "N/A";
    
    try {
      const details = JSON.parse(transaction.payment_method_details);
      if (details.type === "card") {
        const card = details.card;
        return `${card.brand.toUpperCase()} •••• ${card.last4} (${card.exp_month}/${card.exp_year})`;
      }
      return details.type || "N/A";
    } catch (e) {
      return "Error parsing payment details";
    }
  };

  return (
    <div className="transaction-details">
      <Card title={`Stripe Transaction: ${transaction.id}`} bordered={false}>
        <Tabs defaultActiveKey="1">
          <Tabs.TabPane tab="Basic Info" key="1">
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Transaction ID">{transaction.id}</Descriptions.Item>
              <Descriptions.Item label="Charge ID">{transaction.charge_id || "N/A"}</Descriptions.Item>
              <Descriptions.Item label="Gateway">{transaction.gateway_account_name}</Descriptions.Item>
              <Descriptions.Item label="Created Date">{formatDate(transaction.created_date)}</Descriptions.Item>
              <Descriptions.Item label="Status">{transaction.status}</Descriptions.Item>
              <Descriptions.Item label="Type">{transaction.type}</Descriptions.Item>
              <Descriptions.Item label="Description">{transaction.description || "N/A"}</Descriptions.Item>
              <Descriptions.Item label="Payment Method">{getPaymentMethodDetails()}</Descriptions.Item>
              <Descriptions.Item label="Amount">{formatCurrency(transaction.amount, transaction.currency)}</Descriptions.Item>
              <Descriptions.Item label="Net Amount">{formatCurrency(transaction.net_amount, transaction.currency)}</Descriptions.Item>
              <Descriptions.Item label="Fee">{formatCurrency(transaction.fee_amount, transaction.currency)}</Descriptions.Item>
              <Descriptions.Item label="Currency">{transaction.currency}</Descriptions.Item>
              {transaction.refunded && (
                <Descriptions.Item label="Refunded" span={2}>Yes</Descriptions.Item>
              )}
              {transaction.disputed && (
                <Descriptions.Item label="Disputed" span={2}>Yes</Descriptions.Item>
              )}
            </Descriptions>
          </Tabs.TabPane>
          
          {transaction.billing_details && (
            <Tabs.TabPane tab="Billing Details" key="2">
              <Row gutter={16}>
                <Col span={24}>
                  <BillingDetailsTable data={JSON.parse(transaction.billing_details)} />
                </Col>
              </Row>
            </Tabs.TabPane>
          )}
          
          {transaction.shipping_details && (
            <Tabs.TabPane tab="Shipping Details" key="3">
              <Row gutter={16}>
                <Col span={24}>
                  <ShippingDetailsTable data={JSON.parse(transaction.shipping_details)} />
                </Col>
              </Row>
            </Tabs.TabPane>
          )}
          
          {transaction.metadata && (
            <Tabs.TabPane tab="Metadata" key="4">
              <Row gutter={16}>
                <Col span={24}>
                  <MetadataTable data={JSON.parse(transaction.metadata)} />
                </Col>
              </Row>
            </Tabs.TabPane>
          )}
          
          {transaction.outcome && (
            <Tabs.TabPane tab="Outcome" key="5">
              <Row gutter={16}>
                <Col span={24}>
                  <OutcomeTable data={JSON.parse(transaction.outcome)} />
                </Col>
              </Row>
            </Tabs.TabPane>
          )}
          
          <Tabs.TabPane tab="Raw Data" key="6">
            <Row gutter={16}>
              <Col span={24}>
                <Card>
                  <p>Raw transaction data is available but not displayed for readability. Please contact the development team if you need access to specific raw data fields.</p>
                </Card>
              </Col>
            </Row>
          </Tabs.TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

// Helper component to display billing details in a table format
const BillingDetailsTable = ({ data }) => {
  if (!data) return null;
  
  const columns = [
    { title: 'Field', dataIndex: 'field', key: 'field' },
    { title: 'Value', dataIndex: 'value', key: 'value' },
  ];
  
  const tableData = [];
  
  // Add address details if available
  if (data.address) {
    if (data.address.line1) tableData.push({ key: 'address_line1', field: 'Address Line 1', value: data.address.line1 });
    if (data.address.line2) tableData.push({ key: 'address_line2', field: 'Address Line 2', value: data.address.line2 });
    if (data.address.city) tableData.push({ key: 'city', field: 'City', value: data.address.city });
    if (data.address.state) tableData.push({ key: 'state', field: 'State', value: data.address.state });
    if (data.address.postal_code) tableData.push({ key: 'postal_code', field: 'Postal Code', value: data.address.postal_code });
    if (data.address.country) tableData.push({ key: 'country', field: 'Country', value: data.address.country });
  }
  
  // Add other billing details
  if (data.email) tableData.push({ key: 'email', field: 'Email', value: data.email });
  if (data.name) tableData.push({ key: 'name', field: 'Name', value: data.name });
  if (data.phone) tableData.push({ key: 'phone', field: 'Phone', value: data.phone });
  
  return <Table columns={columns} dataSource={tableData} pagination={false} />
};

// Helper component to display shipping details in a table format
const ShippingDetailsTable = ({ data }) => {
  if (!data) return null;
  
  const columns = [
    { title: 'Field', dataIndex: 'field', key: 'field' },
    { title: 'Value', dataIndex: 'value', key: 'value' },
  ];
  
  const tableData = [];
  
  // Add address details if available
  if (data.address) {
    if (data.address.line1) tableData.push({ key: 'address_line1', field: 'Address Line 1', value: data.address.line1 });
    if (data.address.line2) tableData.push({ key: 'address_line2', field: 'Address Line 2', value: data.address.line2 });
    if (data.address.city) tableData.push({ key: 'city', field: 'City', value: data.address.city });
    if (data.address.state) tableData.push({ key: 'state', field: 'State', value: data.address.state });
    if (data.address.postal_code) tableData.push({ key: 'postal_code', field: 'Postal Code', value: data.address.postal_code });
    if (data.address.country) tableData.push({ key: 'country', field: 'Country', value: data.address.country });
  }
  
  // Add other shipping details
  if (data.name) tableData.push({ key: 'name', field: 'Name', value: data.name });
  if (data.phone) tableData.push({ key: 'phone', field: 'Phone', value: data.phone });
  if (data.carrier) tableData.push({ key: 'carrier', field: 'Carrier', value: data.carrier });
  if (data.tracking_number) tableData.push({ key: 'tracking_number', field: 'Tracking Number', value: data.tracking_number });
  
  return <Table columns={columns} dataSource={tableData} pagination={false} />
};

// Helper component to display metadata in a table format
const MetadataTable = ({ data }) => {
  if (!data || Object.keys(data).length === 0) return <p>No metadata available</p>;
  
  const columns = [
    { title: 'Key', dataIndex: 'key', key: 'key' },
    { title: 'Value', dataIndex: 'value', key: 'value' },
  ];
  
  const tableData = Object.entries(data).map(([key, value], index) => ({
    key: index,
    field: key,
    value: typeof value === 'object' ? JSON.stringify(value) : String(value)
  }));
  
  return <Table columns={columns} dataSource={tableData} pagination={false} />
};

// Helper component to display outcome details in a table format
const OutcomeTable = ({ data }) => {
  if (!data) return null;
  
  const columns = [
    { title: 'Field', dataIndex: 'field', key: 'field' },
    { title: 'Value', dataIndex: 'value', key: 'value' },
  ];
  
  const tableData = [];
  
  if (data.network_status) tableData.push({ key: 'network_status', field: 'Network Status', value: data.network_status });
  if (data.reason) tableData.push({ key: 'reason', field: 'Reason', value: data.reason });
  if (data.risk_level) tableData.push({ key: 'risk_level', field: 'Risk Level', value: data.risk_level });
  if (data.risk_score) tableData.push({ key: 'risk_score', field: 'Risk Score', value: data.risk_score });
  if (data.seller_message) tableData.push({ key: 'seller_message', field: 'Seller Message', value: data.seller_message });
  if (data.type) tableData.push({ key: 'type', field: 'Type', value: data.type });
  
  // Add rule details if available
  if (data.rule) {
    tableData.push({ key: 'rule_action', field: 'Rule Action', value: data.rule.action || 'N/A' });
    tableData.push({ key: 'rule_id', field: 'Rule ID', value: data.rule.id || 'N/A' });
  }
  
  return <Table columns={columns} dataSource={tableData} pagination={false} />
};

export default StripeTransactionDetails;
